import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Container,
  IconButton,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import NotesIcon from "@mui/icons-material/Notes";
import {
  Button,
  Snackbar,
  ClearIcon,
  EditIcon,
  PersonIcon,
  WatchIcon,
} from "@verily-src/react-design-system";
import EditEnrollmentDateDialog from "./EditEnrollmentDateDialog";
import EditExitStudyDateDialog from "./EditExitStudyDateDialog";
import { GetParticipantStatusById } from "./ChangeParticipantStatus";
import RemoveStudyParticipantDialog from "./RemoveStudyParticipantDialog";
import InfoSection from "components/InfoSection";
import useStudyParticipantDataLoader from "./StudyParticipantDataLoader";
import { autohideTimeout } from "styles/Time";
import { AppRoutes } from "core/AppRoutes";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useNavigate } from "react-router";
import {
  resetSnackbarMessages,
  setFocusTarget,
  setParticipantSessionSerialized,
  setStudyParticipantSerialized,
  setSuccessMessage,
} from "./StudyParticipantsStateSlice";
import { setParticipantComplianceState } from "participantcompliance/ParticipantComplianceStateSlice";
import { clearCalendarViewCache } from "looker/CalendarViewQueryResultsCache";
import { RemovePicardPrefix } from "common/ResourceName";
import { format } from "date-fns";
import { fromDateProto } from "common/Dates";
import {
  callWithTimeout,
  focusOnRef,
  sxFocusWithin,
} from "common/Accessibility";
import Loading from "components/Loading";
import { StudyParticipant } from "generated/studyparticipant/studyparticipant_pb";
import SDPErrorSnackbar from "components/SDPErrorSnackbar";
import HeaderValuePair from "components/HeaderValuePair";
import { FocusOn } from "./StudyParticipantsStateSlice";
import {
  setParticipantChangeLogState,
  WhichChangeLogsTable,
} from "../common/ChangeLogsStateSlice";
import TopAppBar from "components/TopAppBar";

interface TitleBarProps {
  scrollTarget?: HTMLDivElement;
  studyParticipantId: string;
  onClickExitButton(): void;
  onClickDataChangeLogsButton(): void;
  onClickViewComplianceHistoryButon(): void;
}

// Defines an interface for the functions exposed from TitleBar
export interface TitleBarRef {
  focusOnTitle: () => void;
  focusOnExit: () => void;
}

const TitleBar = forwardRef<TitleBarRef, TitleBarProps>(
  (
    {
      scrollTarget,
      studyParticipantId,
      onClickExitButton,
      onClickDataChangeLogsButton,
      onClickViewComplianceHistoryButon,
    },
    ref
  ) => {
    const titleRef = useRef<HTMLHeadingElement>(null);
    const exitButtonRef = useRef<HTMLButtonElement>(null);

    const focusOnTitle = useCallback(() => {
      titleRef.current?.focus();
    }, []);

    const focusOnExit = useCallback(() => {
      exitButtonRef.current?.focus();
    }, []);

    // Expose functions to parent components.
    useImperativeHandle(
      ref,
      () => ({
        focusOnTitle,
        focusOnExit,
      }),
      [focusOnTitle, focusOnExit]
    );

    const theme = useTheme();
    return (
      <TopAppBar scrollTarget={scrollTarget}>
        <Toolbar
          disableGutters
          sx={{
            background: theme.palette.background.canvas,
            justifyContent: "space-between",
            paddingY: "24px",
            paddingX: "40px",
          }}
        >
          <Box
            sx={{
              display: "inherit",
              alignItems: "center",
            }}
          >
            <Box sx={{ marginRight: "12px", ...sxFocusWithin }}>
              <IconButton
                onClick={onClickExitButton}
                aria-label="Exit"
                ref={exitButtonRef}
              >
                <ClearIcon />
              </IconButton>
            </Box>
            <Typography
              ref={titleRef}
              tabIndex={-1}
              component="h1"
              variant="display5"
            >
              <span style={{ fontFamily: "Inter" }}>Participant </span>
              <span style={{ fontFamily: "Roboto Mono" }}>
                {studyParticipantId}
              </span>
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Button
              variant="outlined"
              onClick={onClickDataChangeLogsButton}
              role="link"
              aria-label="Data change logs"
              label="Data change logs"
            />

            <Button
              variant="filled"
              onClick={onClickViewComplianceHistoryButon}
              role="link"
              aria-label="View compliance history"
              label="View compliance history"
            ></Button>
          </Box>
        </Toolbar>
      </TopAppBar>
    );
  }
);

// The dialog box for viewing Participant details
const StudyParticipantDetail: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const appState = useAppSelector((state) => state.appState);

  // Redirect to home screen when page is refreshed.
  useEffect(() => {
    if (!appState.userConfigured) {
      navigate(AppRoutes.ROOT);
    }
  }, [appState.userConfigured, navigate]);

  const participantsViewState = useAppSelector(
    (state) => state.participantsViewState
  );

  const [hasAssignedDevice, setHasAssignedDevice] = useState(false);

  const [isEditEnrollmentDateDialogOpen, setIsEditEnrollmentDateDialogOpen] =
    useState(false);
  const [isUpdateExitStudyDateDialogOpen, setIsUpdateExitStudyDateDialogOpen] =
    useState(false);
  const [isRemoveParticipantDialogOpen, setIsRemoveParticipantDialogOpen] =
    useState(false);

  const [reloadData, setReloadData] = useState(false);
  const { isLoading, hasError, studyParticipant, participantSession } =
    useStudyParticipantDataLoader(
      participantsViewState.studyParticipantId ?? "",
      reloadData
    );

  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successSnackbarMessage, setSuccessSnackbarMessage] = useState("");

  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState("");

  // Accessibility focus states for buttons
  const titleBarRef = useRef<TitleBarRef>(null);
  const enrollmentDateRef = useRef<HTMLButtonElement>(null);
  const completionDateRef = useRef<HTMLButtonElement>(null);
  const withdrawalDateRef = useRef<HTMLButtonElement>(null);

  const [focusEditParticipantIdBtn, setFocusEditParticipantIdBtn] =
    useState(false);
  const [focusUpdateParticipantStatusBtn, setFocusUpdateParticipantStatusBtn] =
    useState(false);
  const [focusManageWatchButtonBtn, setFocusManageWatchButtonBtn] =
    useState(false);

  // Set initial state when the page is opened (empty dependency array).
  useEffect(() => {
    setFocusEditParticipantIdBtn(false);
    setFocusUpdateParticipantStatusBtn(false);
    setFocusManageWatchButtonBtn(false);
    setShowSuccessSnackbar(false);
    setShowErrorSnackbar(false);
  }, []);

  // Reload participants if there is no study participant ID
  useEffect(() => {
    if (participantsViewState.studyParticipantId === "") {
      navigate(AppRoutes.SITE_PARTICIPANTS);
    }

    setReloadData(true);
  }, [participantsViewState.studyParticipantId, navigate]);

  useEffect(() => {
    if (participantsViewState.successMessage !== "") {
      setShowSuccessSnackbar(true);
      setSuccessSnackbarMessage(participantsViewState.successMessage);
    } else if (participantsViewState.errorMessage !== "") {
      setShowErrorSnackbar(true);
      setErrorSnackbarMessage(participantsViewState.errorMessage);
    }

    dispatch(resetSnackbarMessages());
  }, [
    participantsViewState.successMessage,
    participantsViewState.errorMessage,
    dispatch,
  ]);

  // Update the hasAssignedDevice state when the study participant is loaded.
  useEffect(() => {
    if (studyParticipant.getDeviceId() !== "") {
      setHasAssignedDevice(true);
    } else {
      setHasAssignedDevice(false);
    }

    dispatch(
      setStudyParticipantSerialized(
        Array.from(studyParticipant.serializeBinary())
      )
    );
  }, [studyParticipant, dispatch]);

  // Store participant session data when the study participant is loaded.
  useEffect(() => {
    dispatch(
      setParticipantSessionSerialized(
        Array.from(participantSession.serializeBinary())
      )
    );
  }, [participantSession, dispatch]);

  // Reset reload flag after loading is completed so that the flag
  // can be set to true to trigger a reload next time.
  useEffect(() => {
    if (!isLoading) {
      setReloadData(false);
    }
  }, [isLoading]);

  // Set focus on the component that triggered the update.
  // Add timeout to give snackbar time to be read by screen readers.
  useEffect(() => {
    callWithTimeout(() => {
      switch (participantsViewState.focusTarget) {
        case FocusOn.Title:
          titleBarRef.current?.focusOnTitle();
          break;
        case FocusOn.EditParticipantId:
          setFocusEditParticipantIdBtn(true);
          break;
        case FocusOn.UpdateParticipantStatus:
          setFocusUpdateParticipantStatusBtn(true);
          break;
        case FocusOn.ManageWatch:
          setFocusManageWatchButtonBtn(true);
          break;
        case FocusOn.EditEnrollmentDate:
          focusOnRef(enrollmentDateRef);
          break;
        case FocusOn.EditCompletionDate:
          focusOnRef(completionDateRef);
          break;
        case FocusOn.EditWithdrawalDate:
          focusOnRef(withdrawalDateRef);
          break;
        case FocusOn.Exit:
          titleBarRef.current?.focusOnExit();
          break;
      }
    });
  }, [isLoading, participantsViewState.focusTarget]);

  const handleComplianceHistoryButton = useCallback(() => {
    dispatch(
      setParticipantComplianceState({
        participantId: participantsViewState.studyParticipantId!,
        subjectId: studyParticipant.getSponsorParticipantId(),
        deviceId: studyParticipant.getDeviceId(),
        previousWeekOffset: 0,
        returnRoute: AppRoutes.SITE_PARTICIPANT,
      })
    );
    clearCalendarViewCache();
    navigate(AppRoutes.SITE_PARTICIPANT_COMPLIANCE);
  }, [
    dispatch,
    navigate,
    participantsViewState.studyParticipantId,
    studyParticipant,
  ]);

  const handleClickExitButton = useCallback(() => {
    navigate(AppRoutes.SITE_PARTICIPANTS);
  }, [navigate]);

  const handleClickViewChangeLogsButton = useCallback(() => {
    dispatch(
      setParticipantChangeLogState({
        participantId: participantsViewState.studyParticipantId!,
        sponsorParticipantId: studyParticipant.getSponsorParticipantId(),
        whichChangeLogsTable:
          WhichChangeLogsTable.STUDY_PARTICIPANT_BY_PARTICIPANT_ID,
        returnRoute: AppRoutes.SITE_PARTICIPANT,
      })
    );
    navigate(AppRoutes.STUDY_PARTICIPANT_CHANGE_LOGS_BY_STUDY_PARTICIPANT);
  }, [
    dispatch,
    navigate,
    participantsViewState.studyParticipantId,
    studyParticipant,
  ]);

  function getWatchStatus(): string {
    if (!hasAssignedDevice) {
      return "No watch assigned";
    }

    // If the participant session EndTime has not been reached
    // and the participant is in the Enrolled state,
    // the device is considered active
    const endTime = participantSession.getInterval()?.getEndTime();
    if (
      (!endTime || endTime.toDate() > new Date()) &&
      studyParticipant.getStatus() ===
        StudyParticipant.ParticipantStatus.ENROLLED
    ) {
      return "Active";
    }

    return "Inactive";
  }

  function getWatchStartDate(): string {
    if (!hasAssignedDevice) {
      return "--";
    } else if (participantSession.getInterval()?.getStartTime()) {
      return format(
        participantSession.getInterval()?.getStartTime()?.toDate()!,
        "M/d/yyyy"
      );
    }

    return "--";
  }

  const containerRef = React.useRef<HTMLDivElement>(null);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100vh",
      }}
    >
      <SDPErrorSnackbar
        showSnackbar={showErrorSnackbar}
        setShowSnackbar={setShowErrorSnackbar}
        errorMessage={errorSnackbarMessage}
      />
      <Snackbar
        open={showSuccessSnackbar}
        color="success"
        variant="tonal"
        withIcon={true}
        text={successSnackbarMessage}
        autoHideDuration={autohideTimeout}
        onClose={() => {
          setShowSuccessSnackbar(false);
        }}
        role="alert"
      />
      {isLoading && <Loading />}
      {!isLoading && (
        <>
          <TitleBar
            ref={titleBarRef}
            studyParticipantId={studyParticipant.getSponsorParticipantId()}
            scrollTarget={
              containerRef.current ? containerRef.current : undefined
            }
            onClickExitButton={handleClickExitButton}
            onClickDataChangeLogsButton={handleClickViewChangeLogsButton}
            onClickViewComplianceHistoryButon={handleComplianceHistoryButton}
          />
          <Container role="main" ref={containerRef} sx={{ overflowY: "auto" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignSelf: "center",
                padding: "40px 0px",
              }}
            >
              {hasError && (
                <Alert severity="error" sx={{ paddingLeft: "16px" }}>
                  Failed to load study participant
                </Alert>
              )}
              {!hasError && (
                <>
                  {!hasAssignedDevice && (
                    <Box
                      sx={{
                        alignSelf: "center",
                        marginBottom: "24px",
                        width: "680px",
                      }}
                    >
                      <Alert severity="warning">
                        This participant will be excluded from further
                        compliance calculations until a new watch is assigned.
                      </Alert>
                    </Box>
                  )}
                  <InfoSection
                    title="Participant ID"
                    icon={<PersonIcon />}
                    buttonText="Edit"
                    value={studyParticipant.getSponsorParticipantId()}
                    onClick={() => navigate(AppRoutes.EDIT_PARTICIPANT_ID)}
                    btnFocus={focusEditParticipantIdBtn}
                    btnAriaLabel={`Edit Participant ID ${studyParticipant.getSponsorParticipantId()}`}
                    children={undefined}
                  ></InfoSection>
                  <InfoSection
                    title="Participant status"
                    icon={<NotesIcon />}
                    buttonText="Change status"
                    onClick={() =>
                      navigate(AppRoutes.CHANGE_PARTICIPANT_STATUS)
                    }
                    btnFocus={focusUpdateParticipantStatusBtn}
                    btnAriaLabel={`Change status for Participant ID ${studyParticipant.getSponsorParticipantId()}`}
                  >
                    <Box marginBottom="24px">
                      <HeaderValuePair
                        component="h3"
                        title="Status"
                        value={
                          GetParticipantStatusById(studyParticipant.getStatus())
                            ?.label || ""
                        }
                      />
                    </Box>
                    <Box marginBottom="24px">
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyItems: "center",
                          alignItems: "center",
                        }}
                      >
                        <HeaderValuePair
                          component="h3"
                          title="Enrollment date"
                          value={
                            studyParticipant.getEnrollmentStartTime()
                              ? format(
                                  studyParticipant
                                    .getEnrollmentStartTime()!
                                    .toDate(),
                                  "M/d/yyyy"
                                )
                              : "--"
                          }
                        />
                        <Box
                          sx={{
                            marginLeft: "8px",
                            marginTop: "-24px",
                            ...sxFocusWithin,
                          }}
                        >
                          <IconButton
                            ref={enrollmentDateRef}
                            size="small"
                            onClick={() =>
                              setIsEditEnrollmentDateDialogOpen(true)
                            }
                            aria-label={`Edit enrollment date for Participant ID ${studyParticipant.getSponsorParticipantId()}`}
                          >
                            <EditIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                    <Box marginBottom="24px">
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyItems: "center",
                          alignItems: "center",
                        }}
                      >
                        <HeaderValuePair
                          component="h3"
                          title="Completion date"
                          value={
                            studyParticipant.getStatus() === 2 &&
                            studyParticipant.getExitStudyDate()
                              ? format(
                                  fromDateProto(
                                    studyParticipant.getExitStudyDate()!
                                  ),
                                  "M/d/yyyy"
                                )
                              : "--"
                          }
                        />
                        {studyParticipant.getStatus() === 2 &&
                          studyParticipant.getExitStudyDate() !== undefined && (
                            <Box
                              sx={{
                                marginLeft: "8px",
                                marginTop: "-24px",
                                ...sxFocusWithin,
                              }}
                            >
                              <IconButton
                                ref={completionDateRef}
                                size="small"
                                onClick={() => {
                                  setIsUpdateExitStudyDateDialogOpen(true);
                                }}
                                aria-label={`Edit completion date for Participant ID ${studyParticipant.getSponsorParticipantId()}`}
                              >
                                <EditIcon />
                              </IconButton>
                            </Box>
                          )}
                      </Box>
                    </Box>
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyItems: "center",
                          alignItems: "center",
                        }}
                      >
                        <HeaderValuePair
                          component="h3"
                          title="Withdrawal date"
                          value={
                            studyParticipant.getStatus() === 3 &&
                            studyParticipant.getExitStudyDate()
                              ? format(
                                  fromDateProto(
                                    studyParticipant.getExitStudyDate()!
                                  ),
                                  "M/d/yyyy"
                                )
                              : "--"
                          }
                        />
                        {studyParticipant.getStatus() === 3 &&
                          studyParticipant.getExitStudyDate() !== undefined && (
                            <Box
                              sx={{
                                marginLeft: "8px",
                                marginTop: "-24px",
                                ...sxFocusWithin,
                              }}
                            >
                              <IconButton
                                ref={withdrawalDateRef}
                                size="small"
                                onClick={() => {
                                  setIsUpdateExitStudyDateDialogOpen(true);
                                }}
                                aria-label={`Edit withdrawal date for Participant ID ${studyParticipant.getSponsorParticipantId()}`}
                              >
                                <EditIcon />
                              </IconButton>
                            </Box>
                          )}
                      </Box>
                    </Box>
                  </InfoSection>
                  <InfoSection
                    title="Watch details"
                    icon={<WatchIcon />}
                    buttonText={
                      hasAssignedDevice ? "Manage watch" : "Assign watch"
                    }
                    isButtonDisabled={studyParticipant.getStatus() !== 1}
                    onClick={() =>
                      hasAssignedDevice
                        ? navigate(AppRoutes.MANAGE_WATCH)
                        : navigate(AppRoutes.ASSIGN_WATCH)
                    }
                    btnFocus={focusManageWatchButtonBtn}
                    btnAriaLabel={`${
                      hasAssignedDevice ? "Manage" : "Assign"
                    } watch for Participant ID ${studyParticipant.getSponsorParticipantId()}`}
                  >
                    <Box marginBottom="24px">
                      <HeaderValuePair
                        title="Device ID"
                        value={
                          hasAssignedDevice
                            ? RemovePicardPrefix(studyParticipant.getDeviceId())
                            : "--"
                        }
                        fontFamily="Roboto Mono"
                        component={"h3"}
                      />
                    </Box>
                    <Box marginBottom="24px">
                      <HeaderValuePair
                        component="h3"
                        title="Watch status"
                        value={getWatchStatus()}
                      />
                    </Box>
                    <Box>
                      <HeaderValuePair
                        component="h3"
                        title="Watch start date"
                        value={getWatchStartDate()}
                      />
                    </Box>
                  </InfoSection>
                  <Box
                    sx={{
                      alignSelf: "center",
                      width: "680px",
                    }}
                  >
                    <Button
                      color="destructive"
                      variant="filled"
                      onClick={() => setIsRemoveParticipantDialogOpen(true)}
                      aria-label={`Remove Participant ID ${studyParticipant.getSponsorParticipantId()}`}
                      label="Remove participant"
                    ></Button>
                  </Box>
                </>
              )}
            </Box>
          </Container>
        </>
      )}
      <EditEnrollmentDateDialog
        open={isEditEnrollmentDateDialogOpen}
        studyParticipant={studyParticipant}
        participantSession={participantSession}
        onClose={() => {
          // Do not need to reset the focus button here as
          // dialogs do that natively on close
          setIsEditEnrollmentDateDialogOpen(false);
        }}
        onSuccess={() => {
          dispatch(setFocusTarget(FocusOn.EditEnrollmentDate));
          dispatch(setSuccessMessage("Enrollment date updated"));
          setIsEditEnrollmentDateDialogOpen(false);
          setReloadData(true);
        }}
        onError={(msg: string) => {
          setShowErrorSnackbar(true);
          setErrorSnackbarMessage(msg);
        }}
      ></EditEnrollmentDateDialog>
      <EditExitStudyDateDialog
        open={isUpdateExitStudyDateDialogOpen}
        studyParticipant={studyParticipant}
        onClose={() => {
          // Do not need to reset the focus button here as
          // dialogs do that natively on close
          setIsUpdateExitStudyDateDialogOpen(false);
        }}
        onSuccess={() => {
          switch (studyParticipant.getStatus()) {
            case StudyParticipant.ParticipantStatus.EXIT_STUDY_COMPLETED:
              dispatch(setFocusTarget(FocusOn.EditCompletionDate));
              dispatch(setSuccessMessage("Completion date updated"));
              break;
            case StudyParticipant.ParticipantStatus.EXIT_STUDY_WITHDRAWN:
              dispatch(setFocusTarget(FocusOn.EditWithdrawalDate));
              dispatch(setSuccessMessage("Withdrawal date updated"));
              break;
          }
          setIsUpdateExitStudyDateDialogOpen(false);
          setReloadData(true);
        }}
        onError={(msg: string) => {
          setShowErrorSnackbar(true);
          setErrorSnackbarMessage(msg);
        }}
      ></EditExitStudyDateDialog>
      <RemoveStudyParticipantDialog
        open={isRemoveParticipantDialogOpen}
        studyParticipant={studyParticipant}
        onClose={() => {
          // Do not need to reset the focus button here as
          // dialogs do that natively on close
          setIsRemoveParticipantDialogOpen(false);
        }}
        onSuccess={() => {
          setIsRemoveParticipantDialogOpen(false);

          // Ordering is important here. Need to navigate to study participants
          // BEFORE dispatching success message, otherwise it will be picked up
          // and displayed by StudyParticipantDetails
          navigate(AppRoutes.SITE_PARTICIPANTS);
          dispatch(setSuccessMessage("Participant removed"));
        }}
        onError={(msg: string) => {
          setShowErrorSnackbar(true);
          setErrorSnackbarMessage(msg);
        }}
      ></RemoveStudyParticipantDialog>
    </Box>
  );
};

export default StudyParticipantDetail;
